<template>
  <b-modal v-model="showModal" title="Report Settings" size="lg" hide-footer @shown="fetchReportTypes" @hidden="handleHide">
    <div v-if="reportTypesLoading || storedSettingsLoading" class="text-center py-3">
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">Loading...</span>
      </div>
    </div>
    <div v-else-if="reportTypes.length === 0" class="text-muted text-center py-3">
      No reports available
    </div>
    <div v-else style="max-height: 80vh; overflow-y: auto; position: relative; padding-bottom: 20px;">
      <div v-for="report in reportTypes" :key="report.id" class="mb-3 p-2 mx-3 card-body badge-glow border-secondary border rounded">
        <h6 class="mb-2 text-success font-weight-bold font-italic">{{ report.name }}</h6>

        <!-- Emails -->
        <div class="form-group">
          <label>Report Email Addresses</label>
          <div class="email-tags-container">
            <div class="email-tags-wrapper" :class="(reportSettings[report.id].reportSendEmails.length > 0) ? 'mt-1 mb-2' : ''">
              <div v-for="(email, index) in reportSettings[report.id].reportSendEmails" :key="index" class="email-tag">
                <span>{{ email }}</span>
                <button type="button" class="btn btn-sm btn-icon" @click="removeEmail(report.id, index)">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-x text-danger">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>
            </div>
            <div class="input-group">
              <input
                type="email"
                class="form-control form-control-sm"
                placeholder="Enter email address"
                v-model="newEmails[report.id]"
                @keyup.enter="addEmail(report.id)"
              />
              <div class="input-group-append">
                <button
                  class="btn btn-primary btn-sm"
                  type="button"
                  @click="addEmail(report.id)"
                  :disabled="!isValidEmail(newEmails[report.id])"
                >
                  Add
                </button>
              </div>
            </div>
            <small class="text-muted">Press Enter or click Add to add email addresses</small>
            <small v-if="emailErrors[report.id]" class="text-danger d-block">{{ emailErrors[report.id] }}</small>
          </div>
        </div>

        <div class="row">
          <!-- Type -->
          <div class="col-md-4">
            <div class="form-group">
              <label :for="`report-type-${report.id}`">Type</label>
              <select :id="`report-type-${report.id}`" class="form-control form-control-sm" v-model="reportSettings[report.id].reportExportType">
                <option value="pdf">PDF</option>
                <option value="excel">Excel</option>
              </select>
            </div>
          </div>

          <!-- Status -->
          <div class="col-md-3">
            <div class="form-group">
              <label class="d-block">Status</label>
              <div class="custom-control custom-switch">
                <input
                  type="checkbox"
                  class="custom-control-input"
                  :id="`report-status-${report.id}`"
                  v-model="reportSettings[report.id].status"
                />
                <label class="custom-control-label" :for="`report-status-${report.id}`">
                  {{ reportSettings[report.id].status ? 'Enabled' : 'Disabled' }}
                </label>
              </div>
            </div>
          </div>

          <!-- Send Email To Parent -->
          <div class="col-md-5">
            <div class="form-group">
              <label class="d-block">Include Account Email Address</label>
              <div class="custom-control custom-switch">
                <input
                  type="checkbox"
                  class="custom-control-input"
                  :id="`report-parent-${report.id}`"
                  v-model="reportSettings[report.id].sendEmailToParent"
                />
                <label class="custom-control-label" :for="`report-parent-${report.id}`">
                  {{ reportSettings[report.id].sendEmailToParent ? 'Yes' : 'No' }}
                </label>
              </div>
              <small class="text-muted">If other emails are configured for sending reports.</small>
            </div>
          </div>
        </div>

        <!-- SFTP -->
        <div class="form-group mb-2">
          <label class="d-block">SFTP Upload</label>
          <div class="custom-control custom-switch">
            <input
              type="checkbox"
              class="custom-control-input"
              :id="`report-sftp-${report.id}`"
              v-model="reportSettings[report.id].sftpEnabled"
            />
            <label class="custom-control-label" :for="`report-sftp-${report.id}`">
              {{ reportSettings[report.id].sftpEnabled ? 'Enabled' : 'Disabled' }}
            </label>
          </div>
        </div>

        <div v-if="reportSettings[report.id].sftpEnabled" class="row">
          <div class="col-md-8">
            <div class="form-group">
              <label :for="`report-sftp-host-${report.id}`">SFTP Host</label>
              <input
                type="text"
                class="form-control form-control-sm"
                :id="`report-sftp-host-${report.id}`"
                placeholder="e.g. sftp.example.com"
                v-model.trim="reportSettings[report.id].sftpHost"
              />
            </div>
          </div>
          <div class="col-md-4">
            <div class="form-group">
              <label :for="`report-sftp-port-${report.id}`">SFTP Port</label>
              <input
                type="number"
                min="1"
                max="65535"
                class="form-control form-control-sm"
                :id="`report-sftp-port-${report.id}`"
                placeholder="22"
                v-model.number="reportSettings[report.id].sftpPort"
              />
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group">
              <label :for="`report-sftp-user-${report.id}`">SFTP User</label>
              <input
                type="text"
                class="form-control form-control-sm"
                :id="`report-sftp-user-${report.id}`"
                autocomplete="off"
                v-model.trim="reportSettings[report.id].sftpUser"
              />
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group">
              <label :for="`report-sftp-pass-${report.id}`">SFTP Password</label>
              <input
                type="text"
                class="form-control form-control-sm"
                :id="`report-sftp-pass-${report.id}`"
                autocomplete="new-password"
                v-model="reportSettings[report.id].sftpPass"
              />
            </div>
          </div>
          <div class="col-md-12">
            <div class="form-group">
              <label :for="`report-sftp-path-${report.id}`">SFTP Path</label>
              <input
                type="text"
                class="form-control form-control-sm"
                :id="`report-sftp-path-${report.id}`"
                placeholder="e.g. settlementReports"
                v-model.trim="reportSettings[report.id].sftpPath"
              />
              <small class="text-muted">Remote directory where report files will be uploaded.</small>
            </div>
          </div>
          <div class="col-md-12">
            <small v-if="sftpErrors[report.id]" class="text-danger d-block mb-2">{{ sftpErrors[report.id] }}</small>
          </div>
        </div>

        <div class="d-flex justify-content-end mt-1">
          <b-button
            type="button"
            variant="success"
            size="sm"
            @click="saveReportSetting(report.id)"
            :disabled="reportSaving[report.id]"
          >
            <span v-if="reportSaving[report.id]">Saving...</span>
            <span v-else>Save</span>
          </b-button>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
export default {
  props: {
    show: {
      type: Boolean,
      default: false
    },
    row: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      reportTypes: [],
      reportTypesLoading: false,
      reportTypesFetched: false,
      storedSettings: [],
      storedSettingsLoading: false,
      reportSettings: {},
      reportSaving: {},
      newEmails: {},
      emailErrors: {},
      sftpErrors: {}
    };
  },
  computed: {
    showModal: {
      get() {
        return this.show;
      },
      set(value) {
        if (!value) {
          this.handleHide();
        }
      }
    }
  },
  methods: {
    handleHide() {
      this.$emit('hide');
    },
    fetchReportTypes() {
      // Stored settings are fetched on every open; report types only once
      this.fetchStoredSettings();

      if (this.reportTypesFetched) {
        return;
      }

      this.reportTypesLoading = true;
      this.reportTypes = [];

      this.makeRequest('post', 'v1/get-report-types', {}, {}, false).then(callResponse => {
        this.reportTypesLoading = false;
        if (!callResponse.error) {
          let code = callResponse.response.data.code;
          if (code == 200) {
            this.reportTypes = callResponse.response.data.data || [];
            this.reportTypesFetched = true;
            this.initializeReportSettings();
          }
        }
      });
    },
    fetchStoredSettings() {
      this.storedSettingsLoading = true;
      this.storedSettings = [];

      this.makeRequest('post', 'v1/get-report-setting', { merchantId: parseInt(this.row.id) }, {}, false).then(callResponse => {
        this.storedSettingsLoading = false;
        if (!callResponse.error) {
          let code = callResponse.response.data.code;
          if (code == 200) {
            this.storedSettings = callResponse.response.data.data || [];
          }
        }
        this.initializeReportSettings();
      });
    },
    initializeReportSettings() {
      this.reportSettings = {};
      this.reportSaving = {};
      this.newEmails = {};
      this.emailErrors = {};
      this.sftpErrors = {};

      this.reportTypes.forEach(report => {
        // Match stored setting by report_type_id; unmatched reports fall back to defaults (toggles off)
        const stored = this.storedSettings.find(r => parseInt(r.report_type_id) === parseInt(report.id));
        let settings = {};
        if (stored && stored.settings) {
          settings = typeof stored.settings === 'string' ? this.safeParse(stored.settings) : stored.settings;
        }

        this.$set(this.reportSettings, report.id, {
          status: stored ? parseInt(stored.status) === 1 : false,
          reportExportType: settings.reportExportType || 'pdf',
          reportSendEmails: this.parseEmails(settings.reportSendEmails),
          sendEmailToParent: stored ? parseInt(settings.sendEmailToParent) === 1 : false,
          sftpEnabled: stored ? parseInt(settings.sftpEnabled) === 1 : false,
          sftpHost: settings.sftpHost || '',
          sftpPort: this.parsePort(settings.sftpPort),
          sftpUser: settings.sftpUser || '',
          sftpPass: settings.sftpPass || '',
          sftpPath: settings.sftpPath || ''
        });
        this.$set(this.reportSaving, report.id, false);
        this.$set(this.newEmails, report.id, '');
        this.$set(this.emailErrors, report.id, '');
        this.$set(this.sftpErrors, report.id, '');
      });
    },
    safeParse(str) {
      try {
        const parsed = JSON.parse(str);
        return parsed && typeof parsed === 'object' ? parsed : {};
      } catch (e) {
        return {};
      }
    },
    parsePort(value) {
      const port = parseInt(value);
      return port > 0 && port <= 65535 ? port : 22;
    },
    validateSftp(reportId) {
      const setting = this.reportSettings[reportId];
      if (!setting.sftpEnabled) {
        this.$set(this.sftpErrors, reportId, '');
        return true;
      }
      const port = parseInt(setting.sftpPort);
      let error = '';
      if (!setting.sftpHost) {
        error = 'SFTP host is required';
      } else if (!(port > 0 && port <= 65535)) {
        error = 'SFTP port must be between 1 and 65535';
      } else if (!setting.sftpUser) {
        error = 'SFTP user is required';
      } else if (!setting.sftpPass) {
        error = 'SFTP password is required';
      } else if (!setting.sftpPath) {
        error = 'SFTP path is required';
      }
      this.$set(this.sftpErrors, reportId, error);
      return !error;
    },
    parseEmails(value) {
      if (!value) return [];
      if (Array.isArray(value)) return value.slice();
      if (typeof value === 'string') {
        let str = value.trim();
        try {
          const parsed = JSON.parse(str);
          if (Array.isArray(parsed)) return parsed;
        } catch (e) {
          // Not valid JSON, e.g. "[a@b.com,b@c.com]"
        }
        str = str.replace(/^\[|\]$/g, '');
        return str.split(',').map(v => v.trim().replace(/^"|"$/g, '')).filter(v => v);
      }
      return [];
    },
    isValidEmail(email) {
      if (!email) return false;
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    },
    addEmail(reportId) {
      const raw = (this.newEmails[reportId] || '').trim();
      if (!raw) {
        this.$set(this.emailErrors, reportId, 'Please enter an email address');
        return;
      }
      if (!this.isValidEmail(raw)) {
        this.$set(this.emailErrors, reportId, 'Please enter a valid email address');
        return;
      }
      const email = raw.toLowerCase();
      if (this.reportSettings[reportId].reportSendEmails.includes(email)) {
        this.$set(this.emailErrors, reportId, 'This email is already added');
        return;
      }
      this.reportSettings[reportId].reportSendEmails.push(email);
      this.$set(this.newEmails, reportId, '');
      this.$set(this.emailErrors, reportId, '');
    },
    removeEmail(reportId, index) {
      this.reportSettings[reportId].reportSendEmails.splice(index, 1);
    },
    saveReportSetting(reportId) {
      const setting = this.reportSettings[reportId];
      if (!this.validateSftp(reportId)) {
        return;
      }
      this.$set(this.reportSaving, reportId, true);

      const data = {
        merchantId: parseInt(this.row.id),
        reportTypeId: reportId,
        status: setting.status ? 1 : 0,
        reportExportType: setting.reportExportType,
        reportSendEmails: '[' + setting.reportSendEmails.join(',') + ']',
        sendEmailToParent: setting.sendEmailToParent ? 1 : 0,
        sftpEnabled: setting.sftpEnabled ? 1 : 0
      };

      // SFTP connection details are only sent when SFTP is enabled
      if (setting.sftpEnabled) {
        data.sftpHost = setting.sftpHost;
        data.sftpPort = this.parsePort(setting.sftpPort);
        data.sftpUser = setting.sftpUser;
        data.sftpPass = setting.sftpPass;
        data.sftpPath = setting.sftpPath;
      }

      this.makeRequest('post', 'v1/manage-report-setting', data).then(callResponse => {
        this.$set(this.reportSaving, reportId, false);
        if (!callResponse.error) {
          let code = callResponse.response.data.code;
          if (code == 200) {
            const idx = this.storedSettings.findIndex(r => r.report_type_id === reportId);
            const updated = {
              report_type_id: reportId,
              status: data.status,
              settings: {
                reportExportType: data.reportExportType,
                reportSendEmails: data.reportSendEmails,
                sendEmailToParent: String(data.sendEmailToParent),
                sftpEnabled: String(data.sftpEnabled),
                sftpHost: data.sftpHost || '',
                sftpPort: String(data.sftpPort || 22),
                sftpUser: data.sftpUser || '',
                sftpPass: data.sftpPass || '',
                sftpPath: data.sftpPath || ''
              }
            };
            if (idx > -1) {
              this.$set(this.storedSettings, idx, Object.assign({}, this.storedSettings[idx], updated));
            } else {
              this.storedSettings.push(updated);
            }
            this.$emit('completed', { reportId, data });
          }
        }
      });
    }
  }
};
</script>

<style scoped>
.email-tags-container {
  margin-bottom: 0.5rem;
}

.email-tags-wrapper {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.email-tag {
  display: inline-flex;
  align-items: center;
  background-color: #e9ecef;
  border: 1px solid #dee2e6;
  border-radius: 20px;
  padding: 0.25rem 0.75rem;
  font-size: 0.875rem;
  gap: 0.5rem;
}

.email-tag button {
  padding: 0;
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.input-group {
  margin-bottom: 0.25rem;
}

.text-muted {
  font-size: 0.75rem;
}
</style>
